document.addEventListener('DOMContentLoaded', () => {
  const GIFT_VARIANT_ID = 51649220346177;
  const THRESHOLD_CENTS = 15000; // $150
  const GIFT_FLAG = '_auto_free_gift';
  const DISMISS_KEY = 'freeGiftDismissed';
  const DEBOUNCE_MS = 600;

  let isUpdating = false;
  let debounceTimer = null;

  // ---------- Cart helpers ----------

  async function getCart() {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {
      credentials: 'same-origin'
    });
    return await res.json();
  }

  function isAutoGiftItem(item) {
    return (
      Number(item.variant_id) === GIFT_VARIANT_ID &&
      item.properties &&
      item.properties[GIFT_FLAG] === 'true'
    );
  }

  function qualifyingSubtotal(cart) {
    let subtotal = 0;
    cart.items.forEach(item => {
      if (!isAutoGiftItem(item)) {
        subtotal += item.final_line_price;
      }
    });
    return subtotal;
  }

  function findGiftLine(cart) {
    return cart.items.find(item => isAutoGiftItem(item));
  }

  async function addGift() {
    const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity: 1,
            properties: { [GIFT_FLAG]: 'true' }
          }
        ]
      })
    });
    return await res.json();
  }

  async function removeGift(lineNumber) {
    const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line: lineNumber, quantity: 0 })
    });
    return await res.json();
  }

  // ---------- Dismissal memory ----------

  function isDismissed() {
    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  }

  function setDismissed(val) {
    if (val) {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } else {
      sessionStorage.removeItem(DISMISS_KEY);
    }
  }

  // ---------- Theme refresh dispatch ----------

  async function notifyCartChanged() {
    let cart;
    try {
      cart = await getCart();
    } catch (e) {
      cart = null;
    }

    // 1. Dawn / Horizon PUB_SUB system
    if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'free-gift',
          cartData: cart
        });
      } catch (e) {
        /* noop */
      }
    }

    // 2. Generic events many themes listen for
    ['cart:refresh', 'cart:updated', 'cart:build', 'cart:change',
     'theme:cart:change', 'theme:cart:reload'].forEach(name => {
      document.dispatchEvent(new CustomEvent(name, { detail: { cart } }));
    });

    // 3. Try to re-render the cart drawer directly
    const drawerSelectors = [
      'cart-drawer', 'cart-notification', 'cart-items',
      '.cart-drawer', '#CartDrawer', '[data-cart-drawer]'
    ];

    for (const selector of drawerSelectors) {
      const el = document.querySelector(selector);
      if (el) {
        if (typeof el.renderContents === 'function' && cart) {
          try { el.renderContents(cart); } catch (e) { /* noop */ }
        }
        if (typeof el.onCartUpdate === 'function') {
          try { el.onCartUpdate(); } catch (e) { /* noop */ }
        }
        if (typeof el.refresh === 'function') {
          try { el.refresh(); } catch (e) { /* noop */ }
        }
      }
    }

    // 4. Section re-render fallback — fetches fresh HTML and swaps it in
    try {
      const sectionRes = await fetch(
        '/?sections=cart-drawer,cart-icon-bubble,cart-notification'
      );
      const sections = await sectionRes.json();
      Object.entries(sections).forEach(([id, html]) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const targets = [
          `#shopify-section-${id}`,
          `[data-section-id="${id}"]`,
          `cart-drawer`,
          `cart-notification`
        ];
        for (const targetSel of targets) {
          const existing = document.querySelector(targetSel);
          const fresh = doc.querySelector(targetSel);
          if (existing && fresh) {
            existing.innerHTML = fresh.innerHTML;
            break;
          }
        }
      });
    } catch (e) {
      /* noop */
    }
  }

  // ---------- Core sync ----------

  async function syncFreeGift() {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const cart = await getCart();

      // Nothing to sync on an empty cart
      if (!cart.items || cart.items.length === 0) {
        if (isDismissed()) setDismissed(false);
        return;
      }

      // Validation: if the gift variant is in cart WITHOUT our flag,
      // someone added it manually — strip it out
      const unflaggedGift = cart.items.find(item =>
        Number(item.variant_id) === GIFT_VARIANT_ID &&
        (!item.properties || item.properties[GIFT_FLAG] !== 'true')
      );
      if (unflaggedGift) {
        const badLine = cart.items.findIndex(i => i.key === unflaggedGift.key) + 1;
        if (badLine > 0) {
          await removeGift(badLine);
          await notifyCartChanged();
          return;
        }
      }

      const subtotal = qualifyingSubtotal(cart);
      const qualifies = subtotal >= THRESHOLD_CENTS;
      const giftLine = findGiftLine(cart);

      // Reset dismissal once they drop below the threshold
      if (!qualifies && isDismissed()) {
        setDismissed(false);
      }

      if (qualifies && !giftLine && !isDismissed()) {
        await addGift();
        await notifyCartChanged();
        return;
      }

      if (!qualifies && giftLine) {
        const lineNumber =
          cart.items.findIndex(item => item.key === giftLine.key) + 1;
        if (lineNumber > 0) {
          await removeGift(lineNumber);
          await notifyCartChanged();
        }
      }
    } catch (error) {
      console.error('Free gift sync error:', error);
    } finally {
      isUpdating = false;
    }
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncFreeGift, DEBOUNCE_MS);
  }

  // ---------- Detect manual gift removal ----------

  function watchForManualGiftRemoval(event) {
    const removeBtn = event.target.closest(
      '.cart-remove-button, [data-cart-remove], cart-remove-button'
    );
    if (!removeBtn) return;

    const row = removeBtn.closest('[data-variant-id], [data-line], tr, li');
    if (!row) return;

    const variantAttr = row.getAttribute('data-variant-id');
    if (variantAttr && Number(variantAttr) === GIFT_VARIANT_ID) {
      setDismissed(true);
    }
  }

  // ---------- Listeners ----------

  syncFreeGift();

  // Product forms (add to cart)
  document.body.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches && form.matches('form[action*="/cart/add"]')) {
      scheduleSync();
    }
  });

  // Quantity changes and removes
  document.body.addEventListener('click', event => {
    watchForManualGiftRemoval(event);

    const trigger = event.target.closest(
      '[name="plus"], [name="minus"], .cart-remove-button, ' +
        '.quantity__button, [data-cart-remove], cart-remove-button'
    );
    if (trigger) {
      scheduleSync();
    }
  });

  // Quantity input typing
  document.body.addEventListener('change', event => {
    if (event.target.matches('input.quantity__input, input[name="updates[]"]')) {
      scheduleSync();
    }
  });

  // Fetch interception — catches AJAX cart updates the theme does itself
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

    // Don't re-trigger on our own calls
    if (isUpdating) return response;

    if (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update')
    ) {
      scheduleSync();
    }
    return response;
  };
});